// Turn what is on the chart into an image somebody can send.
//
// Composed in the browser from the chart's OWN canvas -- lightweight-charts
// exposes takeScreenshot(), which already includes the drawings, because they
// are painted by a series primitive onto that same canvas. Nothing is uploaded
// and nothing is rendered server-side: the picture is made where the chart
// already exists, which keeps the zero-external-host rule intact and means a
// share works with no round trip.
//
// The framing matters as much as the picture. A screenshot of candles with a
// line on it is what every trading group is already full of, and it proves
// nothing. This one carries the symbol, the timeframe, the time it was taken,
// and -- when the user's lines sit on levels the engine computed -- the
// arithmetic behind those levels. That is the part nobody else can screenshot,
// because nobody else's chart knows why its lines are there.

const PAD = 16;
const HEADER = 54;
const FOOTER = 46;

function palette() {
  const styles = getComputedStyle(document.documentElement);
  const read = (name, fallback) =>
    (styles.getPropertyValue(name) || "").trim() || fallback;
  return {
    bg: read("--s0", "#080B14"),
    surface: read("--s1", "#0E1320"),
    ink: read("--ink-1", "#F4F7FC"),
    muted: read("--ink-3", "#7E8FAC"),
    gold: read("--gold", "#E9C46A"),
    line: read("--line", "rgba(125,155,205,0.14)"),
  };
}

/**
 * @param {object} opts
 *   chart      -- the IChartApi, for takeScreenshot()
 *   symbol, timeframe
 *   price      -- the last price, or null
 *   drawings   -- the user's own lines, for the caption
 *   zones      -- key levels, so a drawing that sits on one can name it
 *   labels     -- {takenAt, madeWith, disclaimer} already translated
 * @returns {Promise<Blob|null>}
 */
export async function composeShareImage(opts) {
  const { chart, symbol, timeframe, price, drawings = [], zones = [], labels = {} } = opts;
  if (!chart || typeof chart.takeScreenshot !== "function") return null;

  const shot = chart.takeScreenshot();
  if (!shot || !shot.width) return null;

  const colours = palette();
  // The chart's canvas is already in DEVICE pixels. Mixing a CSS-pixel
  // constant into it printed the footer over the candles, clipped the bottom
  // of the chart, and pushed the level caption off the canvas entirely. Work
  // out the device ratio from the canvas itself rather than assuming one.
  const el = chart.chartElement();
  const ratio = el && el.clientWidth ? shot.width / el.clientWidth : 1;
  const width = shot.width;
  const pad = PAD * ratio;
  const header = HEADER * ratio;
  const footer = FOOTER * ratio;
  const caption = captionFor(drawings, zones);
  const captionBand = caption ? 24 * ratio : 0;
  const height = Math.round(shot.height + header + footer + captionBand);

  const out = document.createElement("canvas");
  out.width = width;
  out.height = height;
  const ctx = out.getContext("2d");
  if (!ctx) return null;

  const font = (size, weight) =>
    `${weight || 400} ${Math.round(size * ratio)}px "Inter Tight", system-ui, sans-serif`;

  ctx.fillStyle = colours.bg;
  ctx.fillRect(0, 0, width, height);

  // Header: what this is a picture OF. A screenshot without its symbol and
  // timeframe is the most common way a shared chart misleads.
  ctx.fillStyle = colours.ink;
  ctx.font = font(17, 700);
  ctx.textBaseline = "middle";
  const headerY = header / 2;
  ctx.fillText(symbol || "XAUUSD", pad, headerY);

  const symbolWidth = ctx.measureText(symbol || "XAUUSD").width;
  ctx.font = font(13, 500);
  ctx.fillStyle = colours.gold;
  ctx.fillText(String(timeframe || ""), pad + symbolWidth + 12 * ratio, headerY);

  if (price != null) {
    ctx.fillStyle = colours.ink;
    ctx.font = font(17, 700);
    const text = Number(price).toFixed(2);
    ctx.fillText(text, width - pad - ctx.measureText(text).width, headerY);
  }

  ctx.drawImage(shot, 0, header);

  // The caption: which engine levels the user's own lines are sitting on.
  if (caption) {
    ctx.fillStyle = colours.muted;
    ctx.font = font(12, 500);
    ctx.fillText(caption, pad, header + shot.height + 16 * ratio);
  }

  // Footer: when, and the one sentence that keeps a shared picture from being
  // read as a recommendation.
  const footY = height - footer / 2;
  ctx.strokeStyle = colours.line;
  ctx.lineWidth = Math.max(1, ratio);
  ctx.beginPath();
  ctx.moveTo(0, height - footer);
  ctx.lineTo(width, height - footer);
  ctx.stroke();

  ctx.fillStyle = colours.muted;
  ctx.font = font(11, 400);
  ctx.fillText(labels.takenAt || "", pad, footY);
  const mark = labels.madeWith || "Mithqal";
  ctx.fillStyle = colours.gold;
  ctx.font = font(11, 600);
  ctx.fillText(mark, width - pad - ctx.measureText(mark).width, footY);

  return new Promise((resolve) => out.toBlob(resolve, "image/png"));
}

// Which computed levels the user's lines landed on. A drawing that snapped to
// D-R1 can say so; one drawn in empty space says nothing rather than inventing
// a justification for it.
function captionFor(drawings, zones) {
  const named = [];
  for (const drawing of drawings) {
    for (const anchor of drawing.anchors || []) {
      const zone = (zones || []).find((z) => Math.abs(z.price - anchor.p) < 0.5);
      if (!zone) continue;
      const sources = (zone.sources || []).map((s) => s.source).filter(Boolean).join(" + ");
      if (sources && !named.includes(sources)) named.push(sources);
    }
  }
  return named.length ? named.slice(0, 3).join("  ·  ") : "";
}

// Share it, or fall back to a download. The Web Share API needs a user
// gesture and a secure context; neither is guaranteed, and a share button
// that silently does nothing is worse than one that saves a file.
export async function shareImage(blob, filename, text) {
  if (!blob) return { ok: false, reason: "no_image" };
  const file = new File([blob], filename, { type: "image/png" });
  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text: text || "" });
      return { ok: true, how: "shared" };
    }
  } catch (error) {
    // A user dismissing the share sheet lands here and is not a failure.
    if (error && error.name === "AbortError") return { ok: true, how: "cancelled" };
  }
  try {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return { ok: true, how: "downloaded" };
  } catch (error) {
    return { ok: false, reason: "download_failed" };
  }
}
